import { UserAttributes } from '@optimizely/optimizely-sdk'
import * as optimizelySDK from '@optimizely/react-sdk'
import * as Sentry from '@sentry/browser'
import { initializeRudderStackAnalytics } from 'config/analytics'
import createEnvironment from 'config/env'
import QueryString from 'query-string'
import { find, propEq } from 'ramda'

import {
  ACORNS_HELLO_FLOW,
  OPTIMIZELY_FEATURE_FLAGS,
  OPTIMIZELY_FORCED_VARIATIONS,
  OPTIMIZELY_REMOTE_STRINGS,
  OPTIMIZELY_VARIATIONS,
} from 'app-constants'
import platform from 'components/Trackers/platform'
import trackEvent from 'utils/analytics/trackEvent'
import { getFlow } from 'utils/FlowProvider/utils'
import { eventProxy } from 'utils/Optimizely/eventProxy'
import { setVisitorId } from 'utils/setVisitorId'

import { optimizelyLogger } from './logger'
import { findForcedVariations, getForcedVariation } from './variationsUtils'

let activateOriginal: (experimentKey: string) => string
let getVariationOriginal: (
  experimentKey: string,
  overrideUserId?: string,
  overrideAttributes?: UserAttributes,
) => string
const visitorId = setVisitorId()
const environment = createEnvironment()

const newActivate = (experimentKey: string) => {
  const forcedVariation = getForcedVariation(experimentKey, visitorId)
  if (forcedVariation) {
    return forcedVariation
  }
  return activateOriginal(experimentKey)
}

const newGetVariation = (
  experimentKey: string,
  overrideUserId?: string,
  overrideAttributes?: UserAttributes,
) => {
  const forcedVariation = getForcedVariation(experimentKey, visitorId)
  if (forcedVariation) {
    return forcedVariation
  }
  if (overrideUserId) {
    return getVariationOriginal(experimentKey, overrideUserId)
  }
  return getVariationOriginal(experimentKey)
}

const remoteStringFeatures: Map<string, string> = new Map([
  ['core', 'core_funnel_strings'],
  ['later', 'later_funnel_strings'],
  ['laterOnboarding', 'later_onboarding_strings'],
  ['spend', 'spend_funnel_strings'],
  ['tiered', 'tiered_funnel_strings'],
  ['select_tier', 'select_tier_strings'],
  ['early', 'early_tier_strings'],
  ['event', 'event_strings'],
  ['errors', 'general_error_strings'],
])

type Params = {
  flow?: string
}

export default class InitializeOptimizely {
  private optimizelyClient: any
  private manifestUrl: string
  private frontendEventProxyHost: string
  private params: Params

  constructor(manifestUrl: string, frontendEventProxyHost: string) {
    this.manifestUrl = manifestUrl
    this.frontendEventProxyHost = frontendEventProxyHost
    this.params = QueryString.parse(window.location.search)
  }

  public initialize() {
    return this.createInternalInstance()
  }

  private getDatafile(optimizelyClient: any): any {
    return optimizelyClient?.client?.projectConfigManager?.datafileManager?.currentDatafile
  }

  private segmentExperimentViewed(experiments: any, variationsMap: any) {
    try {
      experiments.forEach(experiment => {
        if (experiment.status === 'Running') {
          const variationName = variationsMap.get(experiment.key)
          const variation = find(propEq('key', variationName), experiment.variations)

          if (variation) {
            try {
              trackEvent('Experiment Viewed', {
                eventData: {
                  object_name: 'optimizelyExperiment',
                  experimentName: experiment.key,
                  experimentId: experiment.id,
                  variationId: variation.id,
                  variationName: variation.key,
                },
              })
            } catch (e) {
              Sentry.addBreadcrumb({
                message: 'Failed to track experiment viewed event. Experiment: ' + experiment.key,
              })
              Sentry.captureException(e)
            }
          }
        }
      })
    } catch (e) {
      Sentry.addBreadcrumb({
        message: 'Failed to track experiments viewed event.',
      })
      Sentry.captureException(e)
    }
  }

  private getStrings(optimizelyClient: any) {
    const strings = window.sessionStorage.getItem(OPTIMIZELY_REMOTE_STRINGS)
    if (!strings) {
      const datafile = this.getDatafile(optimizelyClient)
      if (optimizelyClient && datafile) {
        const allStringsMap = new Map()

        remoteStringFeatures.forEach((featureKey, flow) => {
          const feature = datafile.featureFlags.find(f => f.key === featureKey)

          if (feature) {
            allStringsMap.set(flow, feature.variables)
          }
        })

        const allStrings = JSON.stringify([...allStringsMap.entries()])
        window.sessionStorage.setItem(OPTIMIZELY_REMOTE_STRINGS, allStrings)
      }
    }
  }

  private handleTieredRegFeatureFlag(feature): boolean {
    if (process.env.BUILD_ENV !== 'production' && this.params?.flow?.toLowerCase() === 'core') {
      return false
    } else if (this.params?.flow?.toLowerCase() === 'tiered') {
      return true
    } else {
      return feature.experiments?.[0]?.variations?.[0]?.featureEnabled ?? false
    }
  }

  private isFeatureEnabled(featureKey: string, optimizelyClient: any): boolean {
    const datafile = this.getDatafile(optimizelyClient)
    const featureId = datafile.featureFlags.find(f => f.key === featureKey)?.rolloutId
    const feature = datafile.rollouts.find(r => r.id === featureId)
    if (optimizelyClient) {
      if (featureKey === 'tiered_registration') {
        return this.handleTieredRegFeatureFlag(feature)
      }
      return optimizelyClient.isFeatureEnabled(featureKey)
    }
    return false
  }

  private getFeatureFlags(optimizelyClient: any) {
    const strings = window.sessionStorage.getItem(OPTIMIZELY_FEATURE_FLAGS)
    if (!strings) {
      const datafile = this.getDatafile(optimizelyClient)
      if (optimizelyClient && datafile) {
        const featureFlags = {}
        datafile.featureFlags.forEach(feature => {
          featureFlags[feature.key] = {
            variables: feature.variables,
            isFeatureEnabled: this.isFeatureEnabled(feature.key, optimizelyClient),
          }
        })
        const allStrings = JSON.stringify(featureFlags)

        window.sessionStorage.setItem(OPTIMIZELY_FEATURE_FLAGS, allStrings)
      }
    }
  }

  private setForcedVariationsMap(optimizelyClient: any) {
    /* istanbul ignore else */
    const datafile = this.getDatafile(optimizelyClient)

    const experiments = datafile?.experiments

    window.sessionStorage.removeItem(OPTIMIZELY_FORCED_VARIATIONS)

    const forcedVariations = findForcedVariations(this.params, experiments)

    let forcedVariationsMap = '[]'
    /* istanbul ignore else */
    const newForcedVariationsMap = new Map()
    /* istanbul ignore else */
    if (optimizelyClient && datafile) {
      experiments.forEach(experiment => {
        /* istanbul ignore else */
        if (experiment.status === 'Running') {
          try {
            // Disabling ESLint for this line for no reason other than the code existed like this before this ESLint rule went into effect.
            // If you're here from the future, please resolve this ESLint error!
            // eslint-disable-next-line no-prototype-builtins
            if (forcedVariations.hasOwnProperty(experiment.key)) {
              experiment.variations.forEach(variation => {
                if (
                  variation.key.toLowerCase() === forcedVariations[experiment.key].toLowerCase()
                ) {
                  optimizelyClient.setForcedVariation(experiment.key, setVisitorId(), variation.key)

                  newForcedVariationsMap.set(experiment.key, variation.key)
                }
              })
            }
          } catch (e) {
            Sentry.addBreadcrumb({
              message: `Failed to force user into  variation for the ${experiment.key} experiment`,
            })
            Sentry.captureException(e)
          }
        }
      })

      if (newForcedVariationsMap.size) {
        forcedVariationsMap = JSON.stringify(Array.from(newForcedVariationsMap.entries()))
        window.sessionStorage.setItem(OPTIMIZELY_FORCED_VARIATIONS, forcedVariationsMap)
      }
    }

    return forcedVariationsMap
  }

  private setVariationsMap(optimizelyClient: any) {
    let variationsMap = window.sessionStorage.getItem(OPTIMIZELY_VARIATIONS)
    /* istanbul ignore else */
    if (!variationsMap) {
      const newVariationsMap = new Map()
      /* istanbul ignore else */
      const datafile = this.getDatafile(optimizelyClient)
      if (optimizelyClient && datafile) {
        const { experiments } = datafile
        experiments.forEach(experiment => {
          /* istanbul ignore else */
          if (experiment.status === 'Running') {
            try {
              newVariationsMap.set(
                experiment.key,
                optimizelyClient.getVariation(experiment.key, setVisitorId(), {
                  environment: process.env.OPTIMIZELY_ENVIRONMENT,
                  platform,
                  flow: getFlow(),
                }),
              )
            } catch (e) {
              Sentry.addBreadcrumb({
                message:
                  'Failed to get user variation from Optimizely experiment ' + experiment.key,
              })
              Sentry.captureException(e)
            }
          }
        })

        variationsMap = JSON.stringify(Array.from(newVariationsMap.entries()))

        // TODO: this is deprecated - we don't want to mark experiments as viewed if we don't know
        // for sure that the user has viewed the feature/experiment in question
        // this.segmentExperimentViewed(experiments, newVariationsMap)
        window.sessionStorage.setItem('OPTIMIZELY_VARIATIONS', variationsMap)
      } else {
        variationsMap = '[]'
      }
    }
    return variationsMap
  }

  private createInternalInstance() {
    const optimizelyClient = optimizelySDK.createInstance({
      sdkKey: this.manifestUrl,
      datafileOptions: { urlTemplate: `${this.frontendEventProxyHost}/opt?s=%s` },
      eventDispatcher: eventProxy,
    })

    optimizelySDK.setLogger({ log: optimizelyLogger })

    // Override experiment activation and getVariation
    activateOriginal = optimizelyClient.activate
    activateOriginal = activateOriginal.bind(optimizelyClient)
    getVariationOriginal = optimizelyClient.getVariation
    getVariationOriginal = getVariationOriginal.bind(optimizelyClient)
    optimizelyClient.activate = newActivate.bind(optimizelyClient)
    optimizelyClient.getVariation = newGetVariation.bind(optimizelyClient)

    optimizelyClient
      .onReady()
      .then(() => {
        const params = QueryString.parse(location.search)
        const defaultFlows = ['tiered', 'core']
        if (process.env.BUILD_ENV !== 'production' && defaultFlows.includes(params?.flow)) {
          window.localStorage.removeItem(ACORNS_HELLO_FLOW)
          window.sessionStorage.removeItem(OPTIMIZELY_FEATURE_FLAGS)
        }
        this.setForcedVariationsMap(optimizelyClient)
        this.getFeatureFlags(optimizelyClient)

        this.setVariationsMap(optimizelyClient)
        this.getStrings(optimizelyClient)
      })
      .finally(() => {
        initializeRudderStackAnalytics(
          environment.get('rudderStackWriteKey'),
          environment.get('rudderStackDataPlaneUrl'),
        )
      })

    this.optimizelyClient = optimizelyClient

    return this.optimizelyClient
  }
}
